import {useState} from 'react'
import {Banner, Dialog} from '@primer/react'
import {useQuery} from '@github-ui/react-query'
import sudo from '@github-ui/sudo'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {usePaths} from '../contexts/PathsContext'
import {useButton} from '../contexts/ButtonContext'
import {IsLoadingContext} from '../contexts/IsLoadingContext'
import {UpdatePreviewsProvider} from '../contexts/UpdatePreviewsContext'
import {useEnforcement} from '../contexts/EnforcementContext'
import {useEditablePreviews} from '../hooks/use-editable-previews'
import {PreviewsTable} from './previews-table'
import {EnforcementPicker} from './enforcement-picker'
import {PreviewsCountSummary} from './previews-count-summary'
import type {PreviewPayload, SimpleRuleset} from '../types'

type DialogWrapperProps = {
  onClose: React.ComponentProps<typeof Dialog>['onClose']
  onSuccess: () => void
}

export function DialogWrapper({onClose, onSuccess}: DialogWrapperProps) {
  const {buttonRef} = useButton()
  const {createUrl, previewUrl} = usePaths()
  const {editablePreviews: previews, initializePreviews, setRulesetName, togglePreview} = useEditablePreviews()
  const {enforcement} = useEnforcement()
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [isCreating, setIsCreating] = useState(false)
  const {isFetching} = useQuery({
    queryKey: [previewUrl],
    queryFn: async () => {
      const response = await reactFetchJSON(previewUrl)

      if (response.ok) {
        try {
          const data = (await response.json()) as PreviewPayload
          initializePreviews(data)
          return data
        } catch {
          throw new Error('Failed to load previews.')
        }
      } else if (response.status === 408) {
        throw new Error('Your request timed out.')
      } else {
        throw new Error('Failed to load previews.')
      }
    },
    enabled: !!previewUrl,
  })

  async function handleCreateRulesets() {
    setIsCreating(true)
    setErrorMessage(null)
    if (!(await sudo({target: createUrl, method: 'POST'}))) {
      setErrorMessage('Unauthorized')
      setIsCreating(false)
      return
    }

    const enabledPreviews = previews.filter(preview => preview.isEnabled)
    const rulesets = enabledPreviews
      .reduce((acc, preview) => [...acc, ...preview.rulesets], [] as SimpleRuleset[])
      .map(ruleset => ({...ruleset, enforcement}))
    const body = {
      protected_branch_ids_to_delete: enabledPreviews.map(preview => preview.id),
      rulesets,
    }

    const response = await reactFetchJSON(createUrl, {
      method: 'POST',
      body,
    })

    try {
      const data = await response.json()

      if (response.ok) {
        onSuccess()
      } else {
        setErrorMessage(data.message ?? 'Failed to create rulesets.')
      }
    } catch {
      setErrorMessage('Failed to create rulesets.')
    } finally {
      setIsCreating(false)
    }
  }

  return (
    <Dialog
      title="Review import"
      width="large"
      height="large"
      returnFocusRef={buttonRef}
      onClose={onClose}
      footerButtons={[
        {
          buttonType: 'default',
          content: 'Cancel',
          onClick: _ev => onClose('close-button'),
        },
        {
          buttonType: 'primary',
          content: 'Create rulesets',
          loading: isFetching || isCreating,
          onClick: _ev => handleCreateRulesets(),
        },
      ]}
    >
      <Dialog.Body>
        <IsLoadingContext value={isFetching}>
          <UpdatePreviewsProvider togglePreview={togglePreview} setRulesetName={setRulesetName}>
            <div className="d-flex flex-column gap-3">
              {errorMessage && (
                <Banner title="Error" hideTitle variant="critical">
                  <Banner.Description>{errorMessage}</Banner.Description>
                </Banner>
              )}
              <PreviewsCountSummary previews={previews} />
              <EnforcementPicker />
              <PreviewsTable previews={previews} />
            </div>
          </UpdatePreviewsProvider>
        </IsLoadingContext>
      </Dialog.Body>
    </Dialog>
  )
}
